import { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import api, { formatApiErrorDetail } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import { useCountdown } from "../hooks/useCountdown";
import { useAuctionWebSocket } from "../hooks/useAuctionWebSocket";
import { CountdownPanel } from "../components/auction/CountdownPanel";
import { CurrentBidPanel } from "../components/auction/CurrentBidPanel";
import { BidForm } from "../components/auction/BidForm";
import { BidHistory } from "../components/auction/BidHistory";

export default function AuctionDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [auction, setAuction] = useState(null);
  const [loading, setLoading] = useState(true);
  const [bidAmount, setBidAmount] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const fetchAuction = useCallback(async () => {
    try {
      const { data } = await api.get(`/auctions/${id}`);
      setAuction(data);
      const min =
        data.current_bid != null
          ? data.current_bid + data.min_increment
          : data.starting_price;
      setBidAmount((prev) => prev || String(min));
    } catch (_e) {
      toast.error("Auction not found");
      navigate("/auctions");
    } finally {
      setLoading(false);
    }
  }, [id, navigate]);

  useEffect(() => {
    fetchAuction();
  }, [fetchAuction]);

  // WebSocket handlers
  const handleSnapshot = useCallback((a) => {
    setAuction((prev) => ({ ...(prev || {}), ...a, bids: prev?.bids || [] }));
  }, []);

  const handleBidMsg = useCallback((msg) => {
    setAuction((prev) => {
      if (!prev) return prev;
      // Dedupe: bid may already be present from post-bid fetchAuction() refetch
      const existingBids = prev.bids || [];
      const alreadyPresent = existingBids.some((b) => b.id === msg.bid?.id);
      return {
        ...prev,
        current_bid: msg.current_bid,
        current_bidder_name: msg.current_bidder_name,
        bid_count: msg.bid_count,
        end_time: msg.end_time,
        bids: alreadyPresent ? existingBids : [msg.bid, ...existingBids],
      };
    });
  }, []);

  const { connected: liveConnected } = useAuctionWebSocket(id, {
    onSnapshot: handleSnapshot,
    onBid: handleBidMsg,
  });

  // Polling fallback — runs only when WebSocket isn't connected
  useEffect(() => {
    if (liveConnected) return;
    const interval = setInterval(fetchAuction, 5000);
    return () => clearInterval(interval);
  }, [liveConnected, fetchAuction]);

  const { days, hours, minutes, seconds, ended } = useCountdown(
    auction?.end_time || new Date().toISOString()
  );

  const handleBid = async (e) => {
    e.preventDefault();
    if (!user) {
      toast.error("Please sign in to bid");
      navigate("/login");
      return;
    }
    setSubmitting(true);
    try {
      await api.post(`/auctions/${id}/bid`, { amount: parseFloat(bidAmount) });
      toast.success("Bid placed");
      setBidAmount("");
      await fetchAuction();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Bid failed");
    } finally {
      setSubmitting(false);
    }
  };

  if (loading || !auction) return <div className="p-24 label-eyebrow text-[#555]">Loading…</div>;

  const isLive = auction.status === "live" && !ended;
  const currentBid = auction.current_bid ?? auction.starting_price;
  const minimumBid =
    auction.current_bid != null
      ? auction.current_bid + auction.min_increment
      : auction.starting_price;
  const iAmWinning = user && auction.current_bidder_id === user.id;

  let statusLabel = "Ended";
  if (isLive) statusLabel = "Live auction";
  else if (auction.status === "sold") statusLabel = "Sold";

  return (
    <div data-testid="auction-detail-page" className="px-6 md:px-12 lg:px-24 py-16">
      <Link to="/auctions" className="label-eyebrow text-[#555] hover:text-[#1E3A8A]">
        ← All Auctions
      </Link>
      <div className="mt-10 grid grid-cols-12 gap-y-10 gap-x-4 lg:gap-10">
        <div className="col-span-12 lg:col-span-7">
          <div className="bg-[#F2EDE5]">
            <img
              src={auction.artwork_image}
              alt={`${auction.artwork_title} — live African art auction lot${auction.artist_name ? ` by ${auction.artist_name}` : ""}`}
              className="w-full h-auto max-h-[80vh] object-contain"
              data-testid="auction-image"
            />
          </div>
        </div>
        <div className="col-span-12 lg:col-span-5">
          <div className="inline-flex items-center gap-2 label-eyebrow bg-[#1E3A8A] text-[#F4C430] px-3 py-1.5">
            <span className={`w-1.5 h-1.5 rounded-full ${isLive ? "bg-[#F4C430] animate-pulse" : "bg-white/60"}`} />
            {statusLabel}
            {isLive && liveConnected && (
              <span className="ml-2 text-[0.55rem]" data-testid="ws-status">· REAL-TIME</span>
            )}
          </div>
          <h1
            data-testid="auction-title"
            className="font-serif-display text-5xl tracking-tighter leading-none font-medium mt-5 text-[#111]"
          >
            {auction.artwork_title}
          </h1>
          <Link
            to={`/artist/${auction.artist_id}`}
            className="block mt-3 font-serif-display text-xl italic text-[#1E3A8A] hover:underline"
          >
            by {auction.artist_name}
          </Link>

          <CountdownPanel isLive={isLive} days={days} hours={hours} minutes={minutes} seconds={seconds} />

          <CurrentBidPanel
            auction={auction}
            currentBid={currentBid}
            minimumBid={minimumBid}
            iAmWinning={iAmWinning}
          />

          {isLive && user?.id !== auction.artist_id && (
            <BidForm
              minimumBid={minimumBid}
              bidAmount={bidAmount}
              setBidAmount={setBidAmount}
              onSubmit={handleBid}
              submitting={submitting}
              user={user}
            />
          )}

          {!isLive && auction.status === "sold" && (
            <div className="mt-6 border border-[#1E3A8A] bg-[#F4EFE6]/40 p-5">
              <p className="label-eyebrow text-[#1E3A8A]">Won by</p>
              <p className="font-serif-display text-2xl mt-1 text-[#111]">
                {auction.current_bidder_name}
              </p>
              <p className="font-mono-aam text-sm text-[#555] mt-1">
                at ${auction.current_bid?.toLocaleString()}
              </p>
            </div>
          )}
        </div>
      </div>

      <BidHistory bids={auction.bids} />
    </div>
  );
}
